import React, { useEffect, useState } from "react"
import { useParams, useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { AxiosError } from "axios"

import { getRequest } from "../../../helpers"

type VerifyStatus = "loading" | "success" | "error"
type RouteParams = { token?: string }

const VerifyEmailContainer: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { token } = useParams<RouteParams>()
  const [status, setStatus] = useState<VerifyStatus>("loading")
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [seconds, setSeconds] = useState<number>(10)

  useEffect(() => {
    let mounted = true

    const callVerifyEmail = async (): Promise<void> => {
      try {
        await getRequest({ url: `/api/v1/auth/verify-email/${token}` })
        if (!mounted) return
        setStatus("success")
      } catch (error: unknown) {
        if (!mounted) return
        setStatus("error")
        const axiosError = error as AxiosError<{ message?: string }>
        const message = axiosError?.response?.data?.message
        setErrorMessage(message || t("auth.verify.genericError", "Something went wrong, please try again later."))
      }
    }

    if (token) {
      void callVerifyEmail()
    } else {
      setStatus("error")
      setErrorMessage(t("auth.verify.invalidToken", "Invalid or missing token."))
    }

    return () => {
      mounted = false
    }
  }, [token, t])

  // handle redirect on success
  useEffect(() => {
    if (status !== "success") return

    const interval = setInterval(() => {
      setSeconds(s => (s > 0 ? s - 1 : 0))
    }, 1000)

    const timeout = setTimeout(() => {
      history.push("/login")
    }, 10000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [status, history])

  if (status === "loading") {
    return (
      <div className="tw-mx-auto tw-max-w-xl tw-mt-12">
        <div className="tw-rounded-2xl tw-border tw-border-dark-15 tw-bg-white tw-p-6 tw-shadow-sm">
          <h1 className="tw-text-xl tw-font-semibold tw-mb-2">{t("auth.verify.loadingTitle")}</h1>
          <p className="tw-text-sm tw-text-dark-75">{t("auth.verify.loadingText")}</p>
        </div>
      </div>
    )
  }

  if (status === "success") {
    return (
      <div className="tw-mx-auto tw-max-w-xl tw-mt-12">
        <div className="tw-rounded-2xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-p-6 tw-shadow-sm">
          <h1 className="tw-text-2xl tw-font-semibold tw-text-emerald-800 tw-mb-2">{t("auth.verify.successTitle")}</h1>
          <p className="tw-text-sm tw-text-emerald-900 tw-mb-3">{t("auth.verify.successMessage")}</p>
          <p className="tw-text-sm tw-text-dark-75 tw-mb-6">
            {t("auth.verify.redirectLogin", {
              seconds
            })}
          </p>
          <div className="tw-flex tw-gap-3">
            <button className="tw-btn tw-btn-primary" onClick={() => history.push("/login")}>
              {t("auth.verify.goNow")}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-mx-auto tw-max-w-xl tw-mt-12">
      <div className="tw-rounded-2xl tw-border border-danger tw-p-6 tw-shadow-sm">
        <h1 className="tw-text-xl tw-font-semibold tw-mb-2">{t("auth.verify.errorTitle")}</h1>
        <p className="tw-text-sm tw-mb-4">{errorMessage}</p>
        <button className="tw-btn tw-btn-outline tw-border border-danger" onClick={() => history.push("/login")}>
          {t("auth.verify.backToLogin")}
        </button>
      </div>
    </div>
  )
}

export default VerifyEmailContainer
